/**
 * Per-product lot inventory (shop.product_lots) service.
 *
 * Powers:
 *   * apps/sell -- vendor lot CRUD on the Product Editor page.
 *   * apps/shop -- buyer-facing "Available lots" badges on product detail.
 *   * apps/admin -- moderation surface (when an entire vendor is being
 *     audited).
 *
 * Read paths split by audience:
 *   * `listShopProductLotsForVendor`  -- vendor team / admin (all lots, all statuses).
 *   * `listShopProductLotsPublic`     -- anon + buyers (active lots only, via view).
 *
 * Writes go through RPCs so the vendor-membership check + auto-seeding
 * of `remaining_qty = initial_qty` stay in one place.
 */

import { supabase } from '../client';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type {
  ShopProductLot,
  ShopProductLotPublic,
  ShopProductLotStatus,
  ShopProductLotUpdate,
} from './shopTypes';

export interface ListShopProductLotsInput extends KeysetInput {
  productId: string;
  status?: ShopProductLotStatus;
  includeArchived?: boolean;
}

export async function listShopProductLotsForVendor(
  input: ListShopProductLotsInput,
): Promise<ListResult<ShopProductLot>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('product_lots')
    .select('*', selectCountOption(input.countMode))
    .eq('product_id', input.productId);

  if (!input.includeArchived) q = q.is('archived_at', null);
  if (input.status) q = q.eq('status', input.status);

  q = applyKeyset(q, input.cursor, 'created_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopProductLot[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function listShopProductLotsPublic(
  input: KeysetInput & { productId: string },
): Promise<ListResult<ShopProductLotPublic>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('v_product_lots_public')
    .select('*', selectCountOption(input.countMode))
    .eq('product_id', input.productId);

  q = applyKeyset(q, input.cursor, 'created_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopProductLotPublic[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export interface RegisterShopProductLotInput {
  productId: string;
  lotCode: string;
  initialQty: number;
  manufacturedAt?: string | null;
  expiresAt?: string | null;
  coaUrl?: string | null;
  notes?: string | null;
}

export async function registerShopProductLot(
  input: RegisterShopProductLotInput,
): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_register_lot', {
      p_product_id: input.productId,
      p_lot_code: input.lotCode,
      p_initial_qty: input.initialQty,
      p_manufactured_at: input.manufacturedAt ?? null,
      p_expires_at: input.expiresAt ?? null,
      p_coa_url: input.coaUrl ?? null,
      p_notes: input.notes ?? null,
    });
  if (error) throw error;
  return data as string;
}

export interface UpdateShopProductLotInput {
  id: string;
  lotCode?: string;
  manufacturedAt?: string | null;
  expiresAt?: string | null;
  coaUrl?: string | null;
  notes?: string | null;
}

/** Patch a lot's metadata. Quantities are only mutated via shipment allocations or recall. */
export async function updateShopProductLot(
  input: UpdateShopProductLotInput,
): Promise<ShopProductLot> {
  const patch: ShopProductLotUpdate = {};
  if (input.lotCode !== undefined) patch.lot_code = input.lotCode;
  if (input.manufacturedAt !== undefined) patch.manufactured_at = input.manufacturedAt;
  if (input.expiresAt !== undefined) patch.expires_at = input.expiresAt;
  if (input.coaUrl !== undefined) patch.coa_url = input.coaUrl;
  if (input.notes !== undefined) patch.notes = input.notes;
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('product_lots')
    .update(patch)
    .eq('id', input.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopProductLot;
}

export async function recallShopProductLot(id: string, reason: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_vendor_recall_lot', {
      p_lot_id: id,
      p_reason: reason,
    });
  if (error) throw error;
}

export async function archiveShopProductLot(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('product_lots')
    .update({
      archived_at: new Date().toISOString(),
      status: 'archived',
    })
    .eq('id', id);
  if (error) throw error;
}
